import { HrButton } from '@components/button/button';
import {
  HrButtons,
  HrDarkGrey,
  HrFlexContainer,
  HrIconButton,
  HrImageContainer,
  HrParagraph,
  HrPrimary,
  HrSecondary,
  HrTitleH6,
  HrWhite,
  HrlightestGrey,
  Tablet
} from '@components/styles/hr-styles';
import Turnstile, { useTurnstile } from 'react-turnstile';
import { useMutation } from '@tanstack/react-query';
import { cloneDeepWith, intersection, isArray, isEmpty, isObject } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { useBeforeunload } from 'react-beforeunload';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useQuery } from '../../hook/use-query';
import { useRouter } from '../../hook/use-router';
import Toast from '../../pipeline/components/Toast';
import { createBumeranPerfil, getPostulant, updatePostulant } from '../services/api';
import useStore from '../store/useStore';
import { stepOneRequired } from '../utils/constants';
import {
  getPostulantSource,
  mapPostulantApiNavent,
  formatEducationList,
  formatJobsList,
  isFormSubmitPending
} from '../utils/postulant';
import { ApplicationInformation } from './applicationInformation';
import { Header } from './header';
import { ScreenLoadImportCV } from './load/import-cv';
import { ConfirmExit } from './modals/confirm-exit';
import { ModalFaqs } from './modals/faqs';
import { ModalLogin } from './modals/login';
import { MiniModalSupport } from './modals/mini-support';
import { SuccessfulApplication } from './modals/successful-application';
import { SuccessfulUpdate } from './modals/successful-update';
import { ModalSupport } from './modals/support';
import { UpdateProfile } from './modals/update-profile';
import { StepImportCv } from './step-1-import-cv';
import { StepPersonalInformation } from './step-2-personal-information';
import { StepEducationWorkExperience } from './step-3-education-work-experience';
import { Tabs } from './tabs';
import { ModalShowErrors } from './modals/show-errors';
import { DisplaySettingsContext } from '../store/display-settings';
import {
  attachedFilesRequirementsValidation,
  mustShowAttachedFilesComponent
} from '@/postulation_form/components/attached-files/utils';

const GeneralContainer = createGlobalStyle`
  body {
    margin: 0;
    background-color: ${(props) => props.backgroundColor || 'transparent'};
  }
`;

const Flex = styled(HrFlexContainer)`
  padding: 0 32px;

  &.mobile {
    display: none;
  }

  @media (max-width: ${Tablet}) {
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    &.desktop {
      display: none;
    }

    &.mobile {
      display: flex;
    }
  }
`;

export const PostulantForm = observer(
  ({ postulant = {}, cloudflareRecaptcha, action, loading, setPostulant }) => {
    const maxWidth = '1140px';
    const [t] = useTranslation();
    const [showModalSuport, setShowModalSuport] = useState(false);
    const [showMiniModalSuport, setShowMiniModalSuport] = useState(false);
    const [showModalFaqs, setShowModalFaqs] = useState(false);
    const [showModalUpdateProfile, setShowModalUpdateProfile] = useState(false);
    const [showModalUpdateProfileSuccessful, setShowModalUpdateProfileSuccessful] = useState(false);
    const [showModalUpdateProfileApplication, setShowModalUpdateProfileApplication] = useState(
      false
    );
    const [showModalExit, setShowModalExit] = useState(false);
    const [showModalShowErrors, setShowModalShowErrors] = useState(false);
    const turnstile = useTurnstile();

    const {
      register,
      handleSubmit,
      formState,
      control,
      clearErrors,
      setValue,
      setError,
      getValues,
      unregister,
      trigger
      // loading
    } = useForm({
      mode: 'onTouched',
      delayError: 500
    });
    const {
      vacancy,
      currentStep,
      getDataPostulant,
      vacancyRequirement,
      questions,
      filesLoadedList,
      isActionBlockingSubmit
    } = useStore();
    const [showModalLogin, setShowModalLogin] = useState({
      show: false,
      portal: ''
    });
    const _query = useParams();
    const _queryParams = useQuery();
    const source = _queryParams.get('source');
    const [scrollStep2, scrollStep3] = [useRef(null), useRef(null), useRef(null)];
    const {
      errors,
      submitCount,
      dirtyFields,
      isSubmitSuccessful,
      isSubmitting,
      isSubmitted
    } = useFormState({
      control
    });
    const { executeRecaptcha } = useGoogleReCaptcha();
    const theme = {
      primaryColor: HrPrimary,
      secondaryColor: HrWhite,
      tertiaryColor: HrlightestGrey,
      textSpecialColor: HrPrimary,
      textRegularColor: HrSecondary
    };

    const [toastState, setToastState] = useState({
      visible: false,
      message: null,
      typeToast: null
    });
    const [fileSelected, setFileSelected] = useState({ name: '' });

    const [isPendingRecaptchaGeneration, setIsPendingRecaptchaGeneration] = useState(false);

    useRouter();
    const _navigate = useNavigate();
    const location = useLocation();
    const onShowToast = (newToastState) => {
      setToastState(newToastState);
    };

    const onCloseToast = () => {
      setToastState({
        visible: false,
        message: null,
        typeToast: null
      });
    };

    const resetWidgets = () => {
      if (cloudflareRecaptcha) {
        turnstile.reset();
      } else {
        setIsPendingRecaptchaGeneration(false);
      }
    };

    const _updatePostulant = useMutation(updatePostulant, {
      onSuccess: (data) => {
        resetWidgets();
        setShowModalUpdateProfileApplication(true);

        // Aquí va la llamada a bumeran
        const bumeranCheck = getValues('termsConditions.bumeran');
        if (bumeranCheck) {
          const emailPostulant = postulant.email;
          const nationality = getValues('nationality');
          const residenceCountry = getValues('location.country');
          const idVacancy = vacancy._id.$oid;
          createBumeranPerfil(emailPostulant, vacancy.account, idVacancy, residenceCountry, nationality);
        }

        if (data.data.result === 'error') {
          let message = t(`postulant.error.import.${data.data.response}`);
          if (message === `postulant.error.import.${data.data.response}`) {
            message = t(`postulant.error.support`);
          }
          onShowToast({ visible: true, type: 'error', message });
        }
      },
      onError: (error) => {
        resetWidgets();
        let typeError = 'error';
        let messageKey = 'postulant.error.support';

        if (error.response.status === 500) {
          messageKey = 'postulant.error.support';
        } else {
          const { key } = error.response.data.response;
          messageKey = `postulant.error.${key}`;

          if (messageKey === 'postulant.error.postulant_exists') {
            typeError = 'warning';
          }
        }
        sessionStorage.setItem('ErrorBack', error);
        sessionStorage.setItem('keyErrorBack', error.response.data.response.key);

        const message = t(messageKey);
        onShowToast({ visible: true, type: typeError, message });
      }
    });

    const getPostulantExternal = useMutation(getPostulant, {
      onSuccess: (data) => {
        if (data.data.response) {
          const post = mapPostulantApiNavent(data.data.response, 'edit', vacancyRequirement);
          setPostulant(post);
          Object.keys(post).forEach((key) => {
            const _data = post[key];
            if (_data && !isArray(_data)) {
              setValue(key, post[key]);
            } else if (_data && !isArray(_data)) {
              if (['conocimientosNormalizados', 'conocimientosDesnormalizados'].includes(key)) {
                setValue(key, _data || []);
              }
            }
          });
          _navigate({
            pathname: location.pathname,
            replace: true,
            search: location.search,
            state: { currentStep: 2 },
            hash: `#step2`
          });
        }

        if (data.data.result === 'error') {
          let message = t(`postulant.error.import.${data.data.response}`);
          if (message === `postulant.error.import.${data.data.response}`) {
            message = t(`postulant.error.support`);
          }
          onShowToast({ visible: true, type: 'error', message });
        }
      },
      onError: (error) => console.log(error)
    });

    const _next = () => {
      const _step = currentStep >= 2 ? 3 : currentStep + 1;
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      _navigate({
        pathname: `${location.pathname}`,
        replace: true,
        search: location.search,

        hash: `#step${_step}`
      });
    };

    const _prev = () => {
      if (currentStep === 2 || currentStep === 3) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
        const _step = currentStep - 1;
        _navigate({
          pathname: location.pathname,
          replace: true,

          search: location.search,
          hash: `#step${_step}`
        });
      }
    };

    useEffect(() => {
      setValue('vacancy_id', vacancy._id.$oid);
    }, []);

    useEffect(() => {
      Object.keys(postulant).forEach((key) => {
        const _data = postulant[key];
        if (_data && !isArray(_data) && !isObject(_data)) {
          setValue(key, postulant[key]);
        } else if (_data && isArray(_data)) {
          if (['conocimientosNormalizados', 'conocimientosDesnormalizados'].includes(key)) {
            setValue(key, _data || []);
          }
        }
      });
      _navigate({
        pathname: location.pathname,
        replace: true,
        search: location.search,
        state: { currentStep: 2 },
        hash: `#step${2}`
      });
    }, [postulant]);

    useBeforeunload(async (event) => {
      if (!isEmpty(dirtyFields) && !(isSubmitSuccessful && isSubmitted)) {
        event.preventDefault();
        // return '¿Estás seguro que deseas abandonar el formulario de creación de vacante?';
      }
    });

    const onSubmitCreate = (dataPre) => {
      const data = cloneDeepWith(dataPre, (p) => (typeof p === 'string' ? p.trim() : undefined));

      // filter languages by language
      if (data.languages) {
        data.languages = data.languages.filter((language) => language.language);
      }

      data.action = 'edit';
      if (_query.postulantId) {
        data.postulant_id = _query.postulantId;
      }

      if (data.desiredSalary && data.desiredSalary <= 0 && vacancyRequirement.remuneration !== 0) {
        onShowToast({ visible: true, type: 'error', message: 'El salario no es válido' });
        return;
      }

      // Se envía moneda de la vacante
      data.currency = vacancy.currency;

      const filesAttached = data?.documentFiles ?? [];
      const vacancyFilesRequirements = vacancyRequirement.document_files;
      const filesRequirementsValidation = attachedFilesRequirementsValidation(vacancyFilesRequirements, filesAttached);
      if (!filesRequirementsValidation.success) {
        console.error('Attached files validation error:', filesRequirementsValidation.errors);

        onShowToast({
          visible: true,
          type: 'error',
          message: t('postulant.curriculum.errors.general_validation')
        });
      }

      // Parámetros de micrositios
      if (_query.micrositeName) {
        if (
          typeof getDataPostulant.microsites.micrositeId !== 'undefined' &&
          getDataPostulant.microsites.micrositeId
        ) {
          data.microsite_id = getDataPostulant.microsites.micrositeId;
          data.is_multiple = true;
        } else {
          throw new Error(
            `Microsite param is invalid - ${getDataPostulant.microsites.micrositeId}`
          );
        }
      }

      // Parámetros del source
      const { typePortal } = _query;
      data.source = getPostulantSource(source, typePortal);

      // Panel referidos - Parámetro 'ref'
      if (data.source === 'referral') {
        // Panel de referentes
        const referralId = _queryParams.get('ref');
        if (typeof referralId !== 'undefined' && referralId) {
          data.source = 'referral';
          data.ref = referralId;
        } else {
          throw new Error(`Required param 'ReferralId' is invalid - ${referralId} `);
        }
      }

      const educationList = data?.educations;
      if (Array.isArray(educationList)) data.educations = formatEducationList(educationList, 'new');

      const jobsList = data.posts;
      if (Array.isArray(jobsList)) data.posts = formatJobsList(jobsList, 'new');

      setIsPendingRecaptchaGeneration(true);
      _updatePostulant.mutate(data);
    };
    const validateErrorsSteps = (errorss) => {
      const nameFiles = Object.keys(errorss).find((key) => key.startsWith('loadingfiles__'));
      if (nameFiles) {
        setToastState({
          visible: true,
          type: 'warning',
          message: t(`postulant.error.loadingFile`)
        });
        return;
      }
      const arr = [];
      Object.keys(errorss).forEach((key) => {
        arr.push(key);
      });
      const validateStep1 = intersection(arr, stepOneRequired);
      if (validateStep1.length > 0 || errors.posicionesACubrir) {
        _navigate({
          pathname: location.pathname,
          replace: true,
          search: location.search,
          state: { currentStep: 2 },
          hash: '#step2'
        });
      }
    };

    const clickHandlerExecuteRecaptcha = useCallback(
      async (data) => {
        if (!executeRecaptcha) {
          return;
        }

        if (!data.cloudflare_recaptcha_token) {
          const result = await executeRecaptcha('dynamicAction');
          data.recaptcha = result;
        }

        onSubmitCreate(data);
      },
      [executeRecaptcha]
    );

    useEffect(() => {
      if (submitCount === 3) {
        setShowMiniModalSuport(true);
      }

      if (submitCount) {
        validateErrorsSteps(errors);
      }
    }, [submitCount]);

    // TODO: Contemplar a la hora de unificar la creacion y edicion unificar la logica de importar CV
    useEffect(() => {
      if (
        showModalLogin.autoLogin ||
        (getDataPostulant.bumeran && showModalLogin.portal === 'bumeran') ||
        (getDataPostulant.zonajobs && showModalLogin.portal === 'zonajobs')
      ) {
        _navigate({
          pathname: location.pathname,
          replace: true,
          search: location.search,
          state: { currentStep: 2 },
          hash: '#step2'
        });

        getPostulantExternal.mutate({
          apply_with: showModalLogin.apply_with,
          source: showModalLogin.portal,
          token: showModalLogin.token
        });
      }
    }, [getDataPostulant, showModalLogin]);

    useEffect(() => {
      setValue('documentFiles', filesLoadedList);
    }, [filesLoadedList, setValue])

    const toastError = (data) => {
      const nameFiles = Object.keys(data).find((key) => key.startsWith('loadingfiles__'));
      if (nameFiles) {
        setToastState({
          visible: true,
          type: 'warning',
          message: t(`postulant.error.loadingFile`)
        });
        return;
      }
      const keyErrores = [];
      Object.keys(data).forEach((key) => {
        keyErrores.push(key);
      });
      const priorityError = intersection(keyErrores, stepOneRequired);
      setTimeout(async () => {
        const firstField = priorityError.length > 0 ? priorityError[0] : Object.keys(data)[0];
        if (firstField) {
          await trigger(firstField);
        }
      }, 300);
      onShowToast({
        visible: true,
        type: 'error',
        message: `Faltan completar campos obligatorios`
      });
    };

    const {
      language,
      legacy,
      relocation,
      jobs,
      known,
      remuneration,
      salario,
      school,
      educ,
      vacancy_id: vacancyId,
      attached_file: attachedFile,
      available_time: availableTime,
      social_networks: socialNetworks,
      document_files: attachedFilesRequirements
    } = vacancyRequirement;

    const arrRequeriment = [
      language,
      legacy,
      relocation,
      jobs,
      known,
      remuneration,
      salario,
      school,
      educ,
      vacancyId,
      attachedFile,
      availableTime,
      socialNetworks
    ].some((element) => element === 1 || element === 2);

    const hasQuestions = questions.length !== 0;
    const hasAttachedFiles = mustShowAttachedFilesComponent(attachedFilesRequirements);
    const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);

    document.body.style.backgroundColor = '';
    if (darkMode && loading) {
      document.body.style.backgroundColor = '#1A1D2B';
    }

    if (loading)
      return <ScreenLoadImportCV minHeight='90vh' imgWidth='300' flexDirection='column' />;

    return (
      <>
        {/* ESTILOS GENERALES */}
        <GeneralContainer backgroundColor={darkMode ? '#3B3F51' : theme.tertiaryColor} />

        {/* MODAL LOGIN */}
        {showModalLogin.show && (
          <ModalLogin setShowModal={setShowModalLogin} dataModal={showModalLogin} />
        )}
        {showModalSuport && (
          <ModalSupport
            setShowModalSuport={setShowModalSuport}
            setShowModalFaqs={setShowModalFaqs}
            onShowToast={onShowToast}
            formState={formState}
            getValues={getValues}
            cloudflareRecaptcha={cloudflareRecaptcha}
          />
        )}
        {showMiniModalSuport && (
          <MiniModalSupport
            setShowModalShowErrors={setShowModalShowErrors}
            setShowMiniModalSuport={setShowMiniModalSuport}
            trigger={trigger}
          />
        )}
        {showModalFaqs && (
          <ModalFaqs
            setShowModalShowErrors={setShowModalShowErrors}
            setShowModalFaqs={setShowModalFaqs}
            trigger={trigger}
          />
        )}
        {showModalUpdateProfile && (
          <UpdateProfile
            setShowModal={setShowModalUpdateProfile}
            getValues={getValues}
            vacancy={vacancy}
            setShowModalUpdateProfileSuccessful={setShowModalUpdateProfileSuccessful}
          />
        )}
        {showModalUpdateProfileSuccessful && (
          <SuccessfulUpdate setShowModal={setShowModalUpdateProfileSuccessful} vacancy={vacancy} />
        )}
        {showModalUpdateProfileApplication && (
          <SuccessfulApplication
            setShowModal={setShowModalUpdateProfileApplication}
            vacancy={vacancy}
            action={action}
          />
        )}
        {showModalExit && <ConfirmExit setShowModal={setShowModalExit} vacancy={vacancy} />}
        {showModalShowErrors && (
          <ModalShowErrors
            setShowModalShowErrors={setShowModalShowErrors}
            setShowModalSuport={setShowModalSuport}
            errrosForm={errors}
          />
        )}

        {/* HEADER */}
        <Header
          theme={theme}
          maxWidth={maxWidth}
          setShowModalFaqs={setShowModalFaqs}
          setShowModalUpdateProfile={setShowModalUpdateProfile}
          vacancy={vacancy}
          action={action}
        />

        {/* PORTADA */}
        <HrImageContainer borderRadius='0px' height='180px'>
          <img src={getDataPostulant.imgBanner} alt='Portada' />
        </HrImageContainer>

        {/* INFORMACION DE LA APLICACION */}
        <ApplicationInformation
          theme={theme}
          maxWidth={maxWidth}
          vacancy={vacancy}
          action={action}
        />

        {/* tabs */}
        <Tabs
          maxWidth={maxWidth}
          arrRequeriment={arrRequeriment}
          hasQuestions={hasQuestions}
          hasAttachedFiles={hasAttachedFiles}
        />

        {/* BODY */}
        <form key='formVacancy' name='formVacancy'>
          <HrFlexContainer justifyContent='center'>
            <HrFlexContainer
              maxWidth={maxWidth}
              backgroundColor={theme.secondaryColor}
              background={darkMode ? '#1A1D2B' : ''}
              margin='0px 10px'
              padding='16px 0px 32px 0px'
              flexDirection='column'
              gap='48px'
              alignItems='flex-start'
            >
              <HrFlexContainer gap='8px'>
                {(currentStep === 2 || currentStep === 3) && (
                  <Flex justifyContent='flex-end'>
                    <HrTitleH6
                      fontSizeSetting={fontSizeSetting}
                      isRequired
                      color={darkMode ? '#DADADA' : '#767676'}
                    >
                      {t('postulant.requiredFields')}
                    </HrTitleH6>
                  </Flex>
                )}
                <StepImportCv
                  show={currentStep === 1}
                  vacancy={vacancy}
                  showModalLogin={showModalLogin}
                  setShowModalLogin={setShowModalLogin}
                />
                <StepPersonalInformation
                  register={register}
                  unregister={unregister}
                  formState={formState}
                  control={control}
                  postulant={postulant}
                  vacancyRequirement={vacancyRequirement}
                  getValues={getValues}
                  setValue={setValue}
                  loading={getPostulantExternal.isLoading}
                  setError={setError}
                  clearErrors={clearErrors}
                  vacancy={vacancy}
                  show={currentStep === 2}
                  action={action}
                  refStep={scrollStep2}
                  trigger={trigger}
                />
                <StepEducationWorkExperience
                  refStep={scrollStep3}
                  show={currentStep === 3}
                  register={register}
                  formState={formState}
                  control={control}
                  postulant={postulant}
                  getValues={getValues}
                  setValue={setValue}
                  vacancyRequirement={vacancyRequirement}
                  vacancy={vacancy}
                  questions={questions}
                  unregister={unregister}
                  setError={setError}
                  clearErrors={clearErrors}
                  action={action}
                  trigger={trigger}
                  fileSelected={fileSelected}
                  setFileSelected={setFileSelected}
                />
              </HrFlexContainer>
              {!getPostulantExternal.isLoading && (
                <Flex
                  className='desktop'
                  justifyContent={vacancy.status !== 6 ? 'space-between' : 'flex-end'}
                >
                  {vacancy.status !== 6 && (
                    <HrButtons
                      className='postulation-cancel'
                      type='button'
                      category='cancel'
                      size='large'
                      onClick={() => {
                        setShowModalExit(true);
                      }}
                    >
                      {t(`general.button.cancel`)}
                    </HrButtons>
                  )}
                  <HrFlexContainer width='auto'>
                    {currentStep > 1 && (
                      <HrButtons
                        className='postulation-previous-step'
                        type='button'
                        category={darkMode ? 'tertiaryGreenDark' : 'tertiaryGreen'}
                        size='large'
                        style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                        onClick={_prev}
                      >
                        <HrIconButton className='hr-Carret-left' color={HrPrimary} />
                        {t(`general.button.previous`)}
                      </HrButtons>
                    )}

                    {currentStep === 2 && (arrRequeriment || hasQuestions || hasAttachedFiles) && (
                      <HrButtons
                        className='postulation-next-step'
                        type='button'
                        category={darkMode ? 'tertiaryGreenDark' : 'tertiaryGreen'}
                        size='large'
                        style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                        onClick={_next}
                      >
                        {t(`general.button.continue`)}
                        <HrIconButton className='hr-Carret-right' color={HrPrimary} />
                      </HrButtons>
                    )}

                    {(currentStep === 3 ||
                      (currentStep === 2 && !arrRequeriment && !hasQuestions && !hasAttachedFiles)) && (
                      <HrButton
                        className={
                          action === 'edit'
                            ? 'postulation-update-profile'
                            : 'postulation-create-profile'
                        }
                        type='button'
                        name='submit'
                        category='primary'
                        size='large'
                        text={action === 'edit' ? 'Actualizar postulación' : 'Enviar postulación'}
                        style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                        onClick={handleSubmit(clickHandlerExecuteRecaptcha, toastError)}
                        iconColor={HrWhite}
                        loading={isFormSubmitPending({
                          isPendingRequest: _updatePostulant.isLoading,
                          isPendingForm: isSubmitting,
                          isPendingRecaptchaGeneration,
                          isActionBlockingSubmit
                        })}
                        disabled={isFormSubmitPending({
                          isPendingRequest: _updatePostulant.isLoading,
                          isPendingForm: isSubmitting,
                          isPendingRecaptchaGeneration,
                          isActionBlockingSubmit
                        })}
                      />
                    )}
                  </HrFlexContainer>
                </Flex>
              )}

              {!getPostulantExternal.isLoading && (
                <Flex className='mobile'>
                  <HrFlexContainer>
                    {(currentStep === 3 ||
                      (currentStep === 2 && !arrRequeriment && !hasQuestions && !hasAttachedFiles)) && (
                      <HrButton
                        className={
                          action === 'edit'
                            ? 'postulation-update-profile'
                            : 'postulation-create-profile'
                        }
                        type='button'
                        name='submit'
                        category='primary'
                        size='large'
                        text={action === 'edit' ? 'Actualizar postulación' : 'Enviar postulación'}
                        style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                        onClick={handleSubmit(clickHandlerExecuteRecaptcha, toastError)}
                        iconColor={HrWhite}
                        width='100%'
                        loading={isFormSubmitPending({
                          isPendingRequest: _updatePostulant.isLoading,
                          isPendingForm: isSubmitting,
                          isPendingRecaptchaGeneration,
                          isActionBlockingSubmit
                        })}
                        disabled={isFormSubmitPending({
                          isPendingRequest: _updatePostulant.isLoading,
                          isPendingForm: isSubmitting,
                          isPendingRecaptchaGeneration,
                          isActionBlockingSubmit
                        })}
                      />
                    )}
                    {currentStep === 2 && (arrRequeriment || hasQuestions || hasAttachedFiles) && (
                      <HrButtons
                        className='postulation-next-step'
                        type='button'
                        category={darkMode ? 'tertiaryGreenDark' : 'tertiaryGreen'}
                        style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                        size='large'
                        onClick={_next}
                        width='100%'
                      >
                        {t(`general.button.continue`)}
                        <HrIconButton className='hr-Carret-right' color={HrPrimary} />
                      </HrButtons>
                    )}
                  </HrFlexContainer>
                  <HrFlexContainer flexWrap='nowrap'>
                    {vacancy.status !== 6 && (
                      <HrButtons
                        className='postulation-cancel'
                        type='button'
                        category='cancel'
                        style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                        size='large'
                        width='100%'
                        onClick={() => {
                          setShowModalExit(true);
                        }}
                      >
                        {t(`general.button.cancel`)}
                      </HrButtons>
                    )}
                    {currentStep > 1 && (
                      <HrButtons
                        className='postulation-previous-step'
                        type='button'
                        category={darkMode ? 'tertiaryGreenDark' : 'tertiaryGreen'}
                        style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                        size='large'
                        width='100%'
                        onClick={_prev}
                      >
                        <HrIconButton className='hr-Carret-left' color={HrPrimary} />
                        {t(`general.button.previous`)}
                      </HrButtons>
                    )}
                  </HrFlexContainer>
                </Flex>
              )}
            </HrFlexContainer>
          </HrFlexContainer>
          {cloudflareRecaptcha && (
            <Turnstile
              sitekey={cloudflareRecaptcha}
              theme='auto'
              size='normal'
              onSuccess={(token) => {
                setValue('cloudflare_recaptcha_token', token);
                clearErrors('cloudflare_recaptcha_token');
              }}
              onError={() => {
                setToastState({
                  visible: true,
                  type: 'error',
                  message: t(`postulant.error.invalidRecaptcha`)
                });
                setError('cloudflare_recaptcha_token', {
                  type: 'custom',
                  message:
                    'Es posible que Cloudflare esté bloqueado en su red. Le recomendamos contactar a su administrador de sistemas o a su proveedor de servicios de Internet para resolver este inconveniente.'
                });
                resetWidgets();
              }}
              onExpire={() => resetWidgets()}
              onVerify={() => {
                setIsPendingRecaptchaGeneration(false);
              }}
              style={{
                position: 'fixed',
                bottom: '16px',
                right: '16px',
                zIndex: 1000,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px'
              }}
            />
          )}
        </form>

        {toastState.visible && (
          <Toast
            text={toastState.message}
            isVisible={toastState.visible}
            type={toastState.type}
            onClose={() => onCloseToast()}
            msDelay={4000}
          />
        )}

        <HrFlexContainer justifyContent='center' padding='32px 0' gap='8px'>
          <HrParagraph color={HrDarkGrey}>Powered by</HrParagraph>
          <img
            height='30px'
            src={
              darkMode
                ? '../../../../assets/img/app/HiringRoom-logo_footer_negativo.png'
                : '../../../../assets/img/app/HiringRoom-logo_footer.png'
            }
            alt='Logo HiringRoom'
          />
        </HrFlexContainer>
      </>
    );
  }
);
